import React from 'react';
import { useNavigate } from 'react-router';

import { config, moment } from 'data';
import { formatter, url } from 'helpers';
import { invoiceService } from 'services';
import { useLang, useQuery, useTable, useTableQuery } from 'hooks';
import { DashboardLayout } from 'layouts';
import { InvoiceStatus, TableView } from 'components/layout';
import { DateRangePicker, Form, Input, Select, Table } from 'components/ui';
import { TableColumns } from 'types/components';
import { Invoice, InvoiceStatus as EInvoiceStatus } from 'types/models';
import { InvoicesParams } from 'types/services';
import { DatesPartialParam } from 'types/views';

import styles from './styles.module.css';

type TableParams = InvoicesParams & DatesPartialParam;

const initialTableParams: TableParams = {
  //
};

const InvoicesPage = () => {
  const lang = useLang();
  const navigate = useNavigate();
  const table = useTable<Invoice, TableParams>([config.INVOICES_QUERY_KEY], initialTableParams);

  const invoicesParams: InvoicesParams = {
    page: table.page,
    search: table.params.search || undefined,
    amountFrom: table.params.amountFrom || undefined,
    amountTo: table.params.amountTo || undefined,
    status: table.params.status || undefined,
    dateFrom: table.params.dates?.[0] || undefined,
    dateTo: table.params.dates?.[1] || undefined,
  };

  const invoicesQuery = useQuery({
    queryKey: [config.INVOICES_QUERY_KEY, invoicesParams],
    queryFn: () => invoiceService.getInvoices(invoicesParams),
  });

  const handleViewClick = (invoice: Invoice) => () => {
    navigate(url.toInvoice(invoice.id));
  };

  useTableQuery(table, invoicesQuery);

  const columns: TableColumns<Invoice> = [
    {
      title: lang.get('common.fields.id'),
      render: (_, invoice) => formatter.formatId(invoice.displayId),
    }, {
      className: styles.table__date,
      title: lang.get('common.fields.date'),
      render: (_, invoice) => formatter.formatDateTime(invoice.createdDate),
    }, {
      className: styles.table__price,
      title: `${lang.get('common.fields.price')}, ${config.DEFAULT_CURRENCY}`,
      render: (_, invoice) => formatter.formatNumber(invoice.amount),
    }, {
      title: lang.get('common.fields.product'),
      render: (_, invoice) => <Table.Truncate>{invoice.purpose}</Table.Truncate>,
    }, {
      title: lang.get('common.fields.status'),
      render: (_, invoice) => <InvoiceStatus status={invoice.status} />,
    },
  ];

  return (
    <DashboardLayout title={lang.get('invoice.list.title')}>
      <TableView>

        <TableView.Filters<TableParams>
          initialValues={initialTableParams}
          values={table.params}
          withSearch
          onSubmit={table.setParams}
        >
          <Form.Columns>
            <Form.Item name="amountFrom" label={lang.get('common.filters.price')}>
              <Input
                type="number"
                placeholder={lang.get('common.filters.from')}
                suffix={config.DEFAULT_CURRENCY}
              />
            </Form.Item>
            <Form.Item name="amountTo" label={' '}>
              <Input
                type="number"
                placeholder={lang.get('common.filters.to')}
                suffix={config.DEFAULT_CURRENCY}
              />
            </Form.Item>
          </Form.Columns>
          <Form.Item name="status" label={lang.get('common.filters.status')}>
            <Select
              placeholder={lang.get('common.actions.all')}
              options={Object.values(EInvoiceStatus).map((status) => ({
                value: status,
                label: lang.get(`invoice.statuses.${status.toLowerCase()}`),
              }))}
              allowClear
              sort
            />
          </Form.Item>
          <Form.Item name="dates" label={lang.get('common.filters.date')}>
            <DateRangePicker maxDate={moment()} allowEmpty />
          </Form.Item>
        </TableView.Filters>

        <TableView.Table<Invoice>
          table={table}
          columns={columns}
          rowKey={(invoice) => invoice.id}
          loading={invoicesQuery.isLoading}
          clickable
          onRow={(invoice) => ({ onClick: handleViewClick(invoice) })}
        />

      </TableView>
    </DashboardLayout>
  );
};

export default InvoicesPage;
